<template>
  <div :class="$style['map-container']">
    <div ref="mapRef" :class="$style['map']" />
    <div :class="$style['map-overlay']">
      <span :class="$style['map-overlay-title']">
        Geschätzte Ankunftszeit:
      </span>
      <br>
      <span :class="$style['map-overlay-date']">
        {{ formatDate(appointment.timeslot.from, 'dddd, DD.MM.YYYY') }}
      </span>
      <br>
      <span :class="$style['map-overlay-time']">
        Zwischen {{ formatDate(appointment.timeslot.from, 'HH:mm') }} - {{ formatDate(appointment.timeslot.to, 'HH:mm') }} Uhr
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Loader } from '@googlemaps/js-api-loader'
import type { Appointment } from '~/terminierungsportal/types/appointmentTypes'
import locationIconSrc from '@/assets/icons/map_location.png'
import wrenchIconSrc from '@/assets/icons/map_wrench.png'

type Props = {
  appointment: Appointment
  technicianPosition: {
    lat: number
    lng: number
  }
}

const locationIcon = document.createElement('img')
locationIcon.src = locationIconSrc
locationIcon.style.width = '50px'

const wrenchIcon = document.createElement('img')
wrenchIcon.src = wrenchIconSrc
wrenchIcon.style.width = '50px'

const config = useRuntimeConfig()

const mapOptions: google.maps.MapOptions = {
  mapId: config.public.googleMapsMapId,
  center: { lat: 53.551086, lng: 13.993682 },
  zoom: 15,
  disableDefaultUI: true
}

const props = defineProps<Props>()
const { formatDate } = useDate()

const mapRef = useTemplateRef('mapRef')
const map = ref<google.maps.Map>()
const tenantMarker = ref<google.maps.marker.AdvancedMarkerElement>()
const technicianMarker = ref<google.maps.marker.AdvancedMarkerElement>()
const latLngBounds = ref<google.maps.LatLngBounds>()

const getAddressString = (appointment: Appointment) => `${appointment.location.street} ${appointment.location.houseNr}, ${appointment.location.postalCode} ${appointment.location.city}`

const fitMarkersToBounds = () => {
  if (latLngBounds.value) {
    latLngBounds.value.extend(technicianMarker.value.position)
    latLngBounds.value.extend(tenantMarker.value.position)
    map.value?.fitBounds(latLngBounds.value, {
      top: 200,
      bottom: 50
    })
  }
}

const trackComponentView = () => {
  try {
    if (typeof (window as any)._paq !== 'undefined') {
      const contentName = 'AppointmentTechnicianTracking'
      const contentPiece = `Appointment_${props.appointment.id}`

      console.log(`Tracking Piwik content impression: ${contentName} - ${contentPiece}`)
      ;(window as any)._paq.push(['trackContentImpression', contentName, contentPiece])
    } else {
      console.warn('Piwik _paq object not found. Tracking skipped.')
    }
  } catch (error) {
    console.error('Error during Piwik tracking:', error)
  }
}

onMounted(async () => {
  trackComponentView()
  const loader = new Loader({
    apiKey: config.public.googleMapsApiKey,
    libraries: ['core', 'maps', 'marker', 'geocoding']
  })
  const { LatLngBounds } = await loader.importLibrary('core')
  const { Map } = await loader.importLibrary('maps')
  const { AdvancedMarkerElement } = await loader.importLibrary('marker')
  const { Geocoder } = await loader.importLibrary('geocoding')
  const newMap = new Map(mapRef.value, mapOptions)
  map.value = newMap
  const geocoder = new Geocoder()
  const geocodingResult = await geocoder.geocode({ address: getAddressString(props.appointment) })
  if (geocodingResult.results.length) {
    const lat = geocodingResult.results[0].geometry.location.lat()
    const lng = geocodingResult.results[0].geometry.location.lng()
    tenantMarker.value = new AdvancedMarkerElement({ map: newMap, position: { lat, lng }, content: locationIcon })
  }
  technicianMarker.value = new AdvancedMarkerElement({ map: newMap, position: props.technicianPosition, content: wrenchIcon })
  latLngBounds.value = new LatLngBounds()
  fitMarkersToBounds()
})

onUpdated(() => {
  technicianMarker.value.position = props.technicianPosition
  fitMarkersToBounds()
})

</script>

<style module>
.map-container {
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
}

.map {
  width: 100%;
  height: 100%;
  & img {
    filter: saturate(1.4);
  }
}

.map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  max-width: 400px;
  background: var(--color-white);
  padding: var(--space-md);
  margin: var(--space-md);
  border-radius: var( --radius-default);
}

.map-overlay-title {
  color: var(--color-pacificblue);
  font-size: 0.9rem;
  font-weight: bold;
}

.map-overlay-date {
  font-size: 1.2rem;
}

.map-overlay-time {
  font-size: 1.2rem;
  font-weight: bold;
}
</style>
